import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, LoadingState, StatusChip, Column } from '@/components/shared';
import useBreakpoint from '@/hooks/useBreakpoint';
import { FxInvoiceLine } from '@/services/financeInvoices';
import MetricCard from '@/trio-ui/MetricCard';
import SectionCard from '@/trio-ui/SectionCard';
import { FxInvoice } from '@/types/api/finance';

type AvailableProfile = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  developerType: string | null;
};

type Props = {
  invoice: FxInvoice | null;
  lines: FxInvoiceLine[];
  isLoading: boolean;
  profiles: AvailableProfile[];
  onLinkProfile: (lineId: string, profileId: string | null) => void;
};

const fmt = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  const date = new Date(`${d}T00:00:00`);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusMap = {
  paid: { label: 'Paid', color: '#10B981' },
  partial: { label: 'Partial', color: '#F59E0B' },
  open: { label: 'Open', color: '#054AD9' },
  overdue: { label: 'Overdue', color: '#EF4444' },
};

const FinanceXInvoiceDetailComponent = ({
  invoice,
  lines,
  isLoading,
  profiles,
  onLinkProfile,
}: Props): JSX.Element => {
  const { isMobile } = useBreakpoint();
  const [linkingLineId, setLinkingLineId] = useState<string | null>(null);

  if (isLoading || !invoice) return <LoadingState message="Loading invoice..." />;

  const linkingLine = linkingLineId ? lines.find((l) => l.id === linkingLineId) : null;
  const matchedCount = lines.filter((l) => l.profileId).length;
  const unmatchedCount = lines.length - matchedCount;
  const linesTotal = lines.reduce((sum, l) => sum + l.amount, 0);

  const columns: Column<FxInvoiceLine>[] = [
    {
      id: 'description',
      label: 'Description',
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {r.profileId ? (
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
          ) : (
            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
          )}
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {r.description || r.itemName || '—'}
            </Typography>
            {r.profileName && (
              <Typography variant="caption" color="textSecondary">
                Matched: {r.profileName}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'itemName',
      label: 'Item',
      render: (r) => <Typography variant="body2">{r.itemName || '—'}</Typography>,
    },
    {
      id: 'quantity',
      label: 'Hours',
      align: 'right' as const,
      render: (r) => (
        <Typography variant="body2" fontWeight={500}>
          {r.quantity != null ? r.quantity.toFixed(2) : '—'}
        </Typography>
      ),
    },
    {
      id: 'unitPrice',
      label: 'Rate',
      align: 'right' as const,
      render: (r) => (
        <Typography variant="body2">{r.unitPrice != null ? fmt(r.unitPrice) : '—'}</Typography>
      ),
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right' as const,
      render: (r) => (
        <Typography variant="body2" fontWeight={500}>
          {fmt(r.amount)}
        </Typography>
      ),
    },
    {
      id: 'link',
      label: '',
      width: 48,
      render: (r) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setLinkingLineId(r.id);
          }}
          sx={{ color: r.profileId ? '#054AD9' : '#71717A' }}
        >
          <LinkIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <Link
          to="/financex/invoices"
          style={{ textDecoration: 'none', color: '#054AD9', fontSize: 14, fontWeight: 500 }}
        >
          Invoices
        </Link>
        <Typography variant="body2" color="textSecondary">
          /
        </Typography>
        <Typography variant="body2" color="textSecondary">
          #{invoice.invoiceNumber}
        </Typography>
      </Box>

      <PageHeader
        title={`Invoice #${invoice.invoiceNumber}`}
        actions={<StatusChip status={invoice.status} statusMap={statusMap} />}
      />

      {/* Invoice Header Info */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <MetricCard label="Total Amount" value={fmt(invoice.amount)} color="#054AD9" />
        <MetricCard
          label="Open Balance"
          value={fmt(invoice.openBalance)}
          color={invoice.openBalance > 0 ? '#EF4444' : '#10B981'}
        />
        <MetricCard label="Line Items" value={String(lines.length)} />
        <MetricCard label="Lines Total" value={fmt(linesTotal)} />
      </Box>

      {/* Invoice Details */}
      <SectionCard title="Invoice Details">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
            p: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="textSecondary">
              Client
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {invoice.clientName}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Invoice Date
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {fmtDate(invoice.date)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Due Date
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {fmtDate(invoice.dueDate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              Billing Period
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {invoice.billingPeriod ?? '—'}
            </Typography>
          </Box>
          {invoice.memo && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="caption" color="textSecondary">
                Memo
              </Typography>
              <Typography variant="body2">{invoice.memo}</Typography>
            </Box>
          )}
        </Box>
      </SectionCard>

      <Box sx={{ mt: 3 }} />

      {/* Status chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          icon={<CheckCircleOutlineIcon />}
          label={`${matchedCount} matched`}
          size="small"
          color="success"
          variant="outlined"
        />
        {unmatchedCount > 0 && (
          <Chip
            icon={<ErrorOutlineIcon />}
            label={`${unmatchedCount} unmatched`}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
        {invoice.source && invoice.source !== 'manual' && (
          <Chip label={`Source: ${invoice.source}`} size="small" variant="outlined" />
        )}
      </Box>

      <DataTable
        columns={columns}
        data={lines}
        isLoading={isLoading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search line items..."
        searchFields={(r) => `${r.description ?? ''} ${r.profileName ?? ''} ${r.itemName ?? ''}`}
        emptyMessage="No line items for this invoice."
      />

      {/* Link Profile Dialog */}
      <Dialog
        open={!!linkingLineId}
        onClose={() => setLinkingLineId(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={
          isMobile
            ? { sx: { borderRadius: '16px 16px 0 0', mt: 'auto', maxHeight: '90vh' } }
            : {}
        }
      >
        <DialogTitle>
          Link to Contractor Profile
          {linkingLine && (
            <Typography variant="body2" color="textSecondary">
              {linkingLine.description || linkingLine.itemName || '—'} — {fmt(linkingLine.amount)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Autocomplete
            options={profiles}
            getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
            value={profiles.find((p) => p.id === linkingLine?.profileId) ?? null}
            onChange={(_, val) => {
              if (linkingLineId) {
                onLinkProfile(linkingLineId, val?.id ?? null);
                setLinkingLineId(null);
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select contractor profile" size="small" autoFocus />
            )}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLinkingLineId(null)}>Cancel</Button>
          {linkingLine?.profileId && (
            <Button
              color="error"
              onClick={() => {
                if (linkingLineId) {
                  onLinkProfile(linkingLineId, null);
                  setLinkingLineId(null);
                }
              }}
            >
              Unlink
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FinanceXInvoiceDetailComponent;
